import React, { useState, useEffect } from 'react';
import { FaSave, FaFileInvoice, FaFileUpload, FaTable, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import { fetchAPI } from '../../utils/api';
import * as XLSX from 'xlsx';
import { motion, AnimatePresence } from 'framer-motion';
import { showSuccess, showError, showWarning } from '../../utils/toastUtils';

export default function AdminMarks() {
    const [students, setStudents] = useState([]);
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('manual');
    const [exams, setExams] = useState([]);
    const [selectedExam, setSelectedExam] = useState('');
    const [showAddExam, setShowAddExam] = useState(false);
    const [newExamData, setNewExamData] = useState({ name: '', date: new Date().toISOString().split('T')[0] });
    const [excelData, setExcelData] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [studentFilter, setStudentFilter] = useState('');
    const [mediumFilter, setMediumFilter] = useState('All');
    const [uploadResult, setUploadResult] = useState(null);
    const [formData, setFormData] = useState({
        user_id: '',
        class_id: '',
        exam_id: '',
        marks: ''
    });

    const loadData = async () => {
        try {
            const [studentsData, classesData] = await Promise.all([
                fetchAPI('/admin/get_students_for_marks.php'),
                fetchAPI('/admin/get_classes.php')
            ]);
            setStudents(studentsData);
            setClasses(classesData);
        } catch (err) {
            console.error("Failed to load data", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        if (formData.class_id) loadExams(formData.class_id);
    }, [formData.class_id]);

    const loadExams = async (classId) => {
        try {
            const data = await fetchAPI(`/admin/get_exams.php?class_id=${classId}`);
            setExams(data);
            if (data.length > 0) setSelectedExam(data[0].id);
            else setSelectedExam('');
        } catch (err) {
            console.error("Failed to load exams", err);
        }
    };

    const handleCreateExam = async (e) => {
        e.preventDefault();
        try {
            const response = await fetchAPI('/admin/create_exam.php', {
                method: 'POST',
                body: JSON.stringify({
                    class_id: formData.class_id,
                    name: newExamData.name,
                    exam_date: newExamData.date
                })
            });
            setShowAddExam(false);
            setNewExamData({ name: '', date: new Date().toISOString().split('T')[0] });
            await loadExams(formData.class_id);
            setSelectedExam(response.id);
        } catch (err) {
            showError("Error creating exam: " + err.message);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploadResult(null); // Clear previous results

        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const data = XLSX.utils.sheet_to_json(ws);

                // Map keys to match our expected format (case-insensitive)
                const formattedData = data.map(row => {
                    const findKey = (keys) => Object.keys(row).find(k => keys.includes(k.toLowerCase().replace(/[\s_]/g, '')));
                    const indexKey = findKey(['index', 'indexnumber', 'id', 'studentindex']);
                    const marksKey = findKey(['marks', 'mark', 'score', 'result']);

                    return {
                        indexNumber: row[indexKey] || '',
                        marks: row[marksKey] || 0
                    };
                }).filter(r => r.indexNumber);

                setExcelData(formattedData);
            } catch (err) {
                showError("Failed to parse Excel file: " + err.message);
            }
        };
        reader.readAsBinaryString(file);
    };

    const handleBulkSubmit = async () => {
        if (!selectedExam) {
            showWarning("Please select or create an exam first.");
            return;
        }

        if (excelData.length === 0) {
            showWarning("No data to upload. Please select a valid Excel file.");
            return;
        }

        try {
            setUploading(true);
            setUploadResult(null);

            const response = await fetchAPI('/admin/bulk_save_marks.php', {
                method: 'POST',
                body: JSON.stringify({
                    exam_id: selectedExam,
                    data: excelData
                })
            });

            setUploadResult(response);

            // Show summary toast
            if (response.failed_count > 0) {
                showWarning(`Upload complete: ${response.success_count} succeeded, ${response.failed_count} failed`);
            } else {
                showSuccess(`✅ ${response.success_count} students uploaded successfully!`);
                setExcelData([]); // Clear preview if all good
            }

        } catch (err) {
            showError("Bulk upload failed: " + err.message);
        } finally {
            setUploading(false);
        }
    };

    const downloadTemplate = () => {
        const templateData = [
            { 'Index Number': 'SMP00001', 'Marks': 85 },
            { 'Index Number': 'SMP00002', 'Marks': 92 },
            { 'Index Number': 'SMO00001', 'Marks': 78 }
        ];

        const ws = XLSX.utils.json_to_sheet(templateData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Marks');
        XLSX.writeFile(wb, 'marks_upload_template.xlsx');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedExam) {
            showWarning("Please select or create an exam first.");
            return;
        }
        if (!formData.user_id) {
            showWarning("Please select a student.");
            return;
        }
        try {
            await fetchAPI('/admin/save_marks.php', {
                method: 'POST',
                body: JSON.stringify({
                    ...formData,
                    exam_id: selectedExam
                })
            });
            showSuccess('Marks saved successfully!');
            setFormData({ ...formData, marks: '' }); // Clear marks only
        } catch (err) {
            showError("Error: " + err.message);
        }
    };

    if (loading) return <div className="p-8 text-center text-gray-500 font-bold">Loading students and classes...</div>;

    return (
        <div className="max-w-5xl mx-auto space-y-6 pb-20">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white/60 backdrop-blur-xl rounded-xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-white/50">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight font-display mb-2">
                        Results Management
                    </h1>
                    <p className="text-gray-500 font-medium">
                        Upload and manage student examination results.
                    </p>
                </div>

                {/* Tab Switcher                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 text-center"> */}
                <div className="bg-white p-1.5 rounded-xl shadow-sm border border-gray-100 flex gap-2">
                    {[
                        { id: 'manual', label: 'Manual Entry', icon: FaTable },
                        { id: 'excel', label: 'Excel Upload', icon: FaFileUpload },
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-[10px] uppercase tracking-wider transition-all ${activeTab === tab.id
                                ? 'bg-gray-900 text-white shadow-lg shadow-gray-200'
                                : 'text-gray-500 hover:bg-gray-50'
                                }`}
                        >
                            <tab.icon size={12} />
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-xl shadow-gray-200/50 border border-gray-100 p-8 md:p-10">
                {/* Common Header Info for both tabs */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10 pb-10 border-b border-gray-50">
                    <div className="space-y-2">
                        <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Academic Class</label>
                        <select
                            name="class_id"
                            value={formData.class_id}
                            onChange={handleChange}
                            className="w-full bg-gray-50/50 border border-gray-100 rounded-xl p-4 font-bold text-gray-900 focus:bg-white focus:ring-4 focus:ring-blue-500/5 transition-all outline-none"
                            required
                        >
                            <option value="">Select Class</option>
                            {classes.map(c => <option key={c.id} value={c.id}>{c.name} (Grade {c.grade})</option>)}
                        </select>
                    </div>
                    <div className="space-y-2">
                        <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 flex justify-between items-center">
                            Select Exam
                            <button
                                onClick={() => setShowAddExam(!showAddExam)}
                                className="text-blue-600 hover:text-blue-700 normal-case font-bold"
                            >
                                {showAddExam ? "Cancel" : "+ Add New Exam"}
                            </button>
                        </label>

                        {!showAddExam ? (
                            <select
                                value={selectedExam}
                                onChange={(e) => setSelectedExam(e.target.value)}
                                className="w-full bg-gray-50/50 border border-gray-100 rounded-xl p-4 font-bold text-gray-900 focus:bg-white focus:ring-4 focus:ring-blue-500/5 transition-all outline-none"
                                required
                                disabled={!formData.class_id}
                            >
                                <option value="">Select Exam</option>
                                {exams.map(e => <option key={e.id} value={e.id}>{e.name} ({new Date(e.exam_date).toLocaleDateString()})</option>)}
                            </select>
                        ) : (
                            <div className="flex gap-2">
                                <input
                                    placeholder="Exam Name"
                                    value={newExamData.name}
                                    onChange={(e) => setNewExamData({ ...newExamData, name: e.target.value })}
                                    className="flex-1 bg-gray-50/50 border border-gray-100 rounded-xl p-4 font-bold text-gray-900 focus:bg-white outline-none"
                                />
                                <input
                                    type="date"
                                    value={newExamData.date}
                                    onChange={(e) => setNewExamData({ ...newExamData, date: e.target.value })}
                                    className="w-40 bg-gray-50/50 border border-gray-100 rounded-xl p-4 font-bold text-gray-900 focus:bg-white outline-none"
                                />
                                <button
                                    onClick={handleCreateExam}
                                    className="px-6 bg-blue-600 text-white rounded-xl font-bold uppercase text-[10px] tracking-wider"
                                >
                                    Create
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                <AnimatePresence mode="wait">
                    {activeTab === 'manual' ? (
                        <motion.form
                            key="manual"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            onSubmit={handleSubmit}
                            className="space-y-8"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-2">
                                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Select Student</label>
                                    {/* Searchable Student Select */}
                                    <div className="flex bg-gray-50/50 border border-gray-100 rounded-t-xl overflow-hidden border-b-2">
                                        <input
                                            type="text"
                                            placeholder="Type Index Number or Name to Search..."
                                            value={studentFilter}
                                            onChange={(e) => setStudentFilter(e.target.value)}
                                            className="flex-1 bg-transparent p-3 text-sm font-bold text-gray-700 focus:bg-white outline-none"
                                        />
                                        <select
                                            value={mediumFilter}
                                            onChange={(e) => setMediumFilter(e.target.value)}
                                            className="w-32 bg-transparent p-3 text-xs font-bold text-gray-500 focus:bg-white outline-none border-l border-gray-100"
                                        >
                                            <option value="All">All Med</option>
                                            <option value="Sinhala">Sinhala</option>
                                            <option value="English">English</option>
                                        </select>
                                    </div>
                                    <select
                                        name="user_id"
                                        value={formData.user_id}
                                        onChange={handleChange}
                                        className="w-full bg-gray-50/50 border border-gray-100 rounded-b-xl p-4 font-bold text-gray-900 focus:bg-white outline-none"
                                        required
                                        size={studentFilter || mediumFilter !== 'All' ? 5 : 1}
                                    >
                                        <option value="">Select Student</option>
                                        {students
                                            .filter(s => {
                                                const matchesSearch = !studentFilter ||
                                                    (s.name && s.name.toLowerCase().includes(studentFilter.toLowerCase())) ||
                                                    (s.index_number && s.index_number.toLowerCase().includes(studentFilter.toLowerCase())) ||
                                                    (s.email && s.email.toLowerCase().includes(studentFilter.toLowerCase()));
                                                const matchesMedium = mediumFilter === 'All' || s.medium === mediumFilter;
                                                return matchesSearch && matchesMedium;
                                            })
                                            .map(s => (
                                                <option key={s.id} value={s.id}>
                                                    {s.name} {s.index_number ? `(${s.index_number})` : ''} {s.medium ? `[${s.medium}]` : ''}
                                                </option>
                                            ))
                                        }
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Marks Obtained</label>
                                    <input
                                        type="number"
                                        name="marks"
                                        value={formData.marks}
                                        onChange={handleChange}
                                        placeholder="85"
                                        className="w-full bg-gray-50/50 border border-gray-100 rounded-xl p-4 font-bold text-gray-900 focus:bg-white outline-none"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="flex justify-end pt-4">
                                <button
                                    type="submit"
                                    className="px-12 py-4 bg-gray-900 text-white rounded-xl font-black text-[10px] uppercase tracking-[0.2em] transition-all active:scale-95 shadow-xl shadow-gray-200 hover:bg-black flex items-center gap-3"
                                >
                                    <FaSave /> Save Result
                                </button>
                            </div>
                        </motion.form>
                    ) : (
                        <motion.div
                            key="excel"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="space-y-8"
                        >
                            {/* Template Download */}
                            <div className="flex justify-end mb-4">
                                <button
                                    onClick={downloadTemplate}
                                    className="flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-xl font-bold text-[10px] uppercase tracking-wider hover:bg-green-100 transition-all"
                                >
                                    <FaFileInvoice /> Download Template
                                </button>
                            </div>

                            {/* File Upload Area */}
                            <div className="relative group">
                                <input
                                    type="file"
                                    accept=".xlsx, .xls"
                                    onChange={handleFileUpload}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                />
                                <div className="bg-red-50 border-l-4 border-red-500 rounded-xl p-6 mb-6">
                                    <div className="w-16 h-16 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600 mx-auto mb-4 group-hover:scale-110 transition-transform">
                                        <FaFileUpload size={24} />
                                    </div>
                                    <h3 className="font-black text-gray-900 uppercase tracking-widest text-sm mb-2">Drop Excel File Here</h3>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Supports .xlsx and .xls formats</p>
                                </div>
                            </div>

                            {/* Data Preview */}
                            {excelData.length > 0 && (
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-xs font-black text-gray-900 uppercase tracking-widest flex items-center gap-2">
                                            <FaTable className="text-blue-500" /> Preview ({excelData.length} records)
                                        </h3>
                                        <button
                                            onClick={() => setExcelData([])}
                                            className="text-[9px] font-bold text-red-500 uppercase tracking-widest hover:underline"
                                        >
                                            Clear File
                                        </button>
                                    </div>
                                    <div className="max-h-[300px] overflow-auto rounded-xl border border-gray-100 bg-gray-50/30">
                                        <table className="w-full text-left">
                                            <thead className="bg-gray-100 sticky top-0">
                                                <tr>
                                                    <th className="p-4 text-[9px] font-black text-gray-400 uppercase tracking-widest">Index Number</th>
                                                    <th className="p-4 text-[9px] font-black text-gray-400 uppercase tracking-widest">Marks</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-50">
                                                {excelData.map((row, idx) => (
                                                    <tr key={idx} className="hover:bg-white">
                                                        <td className="p-4 font-bold text-gray-900 text-xs">{row.indexNumber}</td>
                                                        <td className="p-4 font-bold text-blue-600 text-xs">{row.marks}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="flex justify-end pt-4">
                                        <button
                                            onClick={handleBulkSubmit}
                                            disabled={uploading}
                                            className="px-12 py-4 bg-blue-600 text-white rounded-xl font-black text-[10px] uppercase tracking-[0.2em] transition-all active:scale-95 shadow-xl shadow-blue-200 hover:bg-blue-700 disabled:opacity-50 flex items-center gap-3"
                                        >
                                            {uploading ? (
                                                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                            ) : (
                                                <FaFileUpload />
                                            )}
                                            {uploading ? 'Processing...' : 'Start Bulk Upload'}
                                        </button>
                                    </div>

                                    {/* Upload Results Report */}
                                    {uploadResult && (
                                        <div className="mt-8 space-y-4 animate-in fade-in slide-in-from-bottom-4">
                                            {uploadResult.failed_count > 0 && (
                                                <div className="bg-red-50 rounded-xl border border-red-100 p-6">
                                                    <h4 className="flex items-center gap-2 text-red-700 font-black text-sm uppercase tracking-wider mb-4">
                                                        <FaExclamationTriangle /> Failed Entries ({uploadResult.failed_count})
                                                    </h4>
                                                    <div className="bg-white rounded-xl border border-red-100 p-4 max-h-40 overflow-y-auto">
                                                        <ul className="space-y-2">
                                                            {uploadResult.errors.map((err, idx) => (
                                                                <li key={idx} className="text-xs text-red-600 font-bold flex items-start gap-2">
                                                                    <span className="mt-0.5">•</span>
                                                                    <span>{err}</span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </div>
                                            )}

                                            {uploadResult.success_count > 0 && (
                                                <div className="bg-green-50 rounded-xl border border-green-100 p-4 flex items-center gap-3">
                                                    <FaCheckCircle className="text-green-600" />
                                                    <span className="text-green-700 font-bold text-xs uppercase tracking-wide">
                                                        Successfully uploaded {uploadResult.success_count} entries
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Formatting Guide */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-blue-50/50 p-6 rounded-xl border border-blue-100/50 flex items-start gap-4">
                    <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-blue-600 shadow-sm flex-shrink-0">
                        <FaCheckCircle size={20} />
                    </div>
                    <div>
                        <h4 className="font-black text-gray-900 uppercase tracking-widest text-xs mb-2">Excel Requirements</h4>
                        <p className="text-[10px] font-bold text-blue-800/60 uppercase leading-relaxed tracking-wider">
                            The sheet must contain two columns: one for **Index Number** and one for **Marks**. Column headers like "Student ID" and "Score" are also automatically detected.
                        </p>
                    </div>
                </div>
                <div className="bg-amber-50/50 p-6 rounded-xl border border-amber-100/50 flex items-start gap-4">
                    <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-amber-600 shadow-sm flex-shrink-0">
                        <FaExclamationTriangle size={20} />
                    </div>
                    <div>
                        <h4 className="font-black text-gray-900 uppercase tracking-widest text-xs mb-2">Duplicate Policy</h4>
                        <p className="text-[10px] font-bold text-amber-800/60 uppercase leading-relaxed tracking-wider">
                            If a student already has a mark for the same exam and class, the new mark will **update** the existing record. No duplicate entries will be created.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
